<template>
    <div class="wallet-content">
        <UIPageHeader
            :title="$t('wallet.title')"
            :okx="true"
            :total="true"
            :totalLabel="wallet.paid"
            :swap="true"
        />

        <div v-if="pendingImex.length" class="wallet-content__imex-banner">
            <span>{{ $t('wallet.imexPendingBanner') }}</span>
            <button
                v-for="w in pendingImex"
                :key="w.id"
                class="wallet-content__imex-resume"
                @click="onResumeImex(w)"
            >{{ $t('wallet.imexFrameOpen') }} ({{ w.coin }} {{ w.amount }})</button>
        </div>

        <div v-if="poolLiquidity !== null" class="wallet-content__pool-liquidity">
            {{ $t('wallet.imexPoolLiquidity', { amount: poolLiquidity }) }}
        </div>

        <div class="wallet-content__body">
            <WalletHeader v-if="!loading"
                :wallet="wallet"
                @deposit="onDeposit"
                @withdraw="onWithdraw"
                @withdrawRub="onWithdrawRub"
                @component="onComponent"
            />

            <WalletTable
                :transactions="transactions"
                :loading="loading"
                :loadingNewPage="loadingNewPage"
                :show="show"
                :detailed="detailed"
                @page="onChangePage"
                @detailed="onChangeDetailed"
            />
        </div>

        <transition name="modal" mode="out-in">
            <component
                class="wallet-content__modal"
                :is="getComponent"
                :currency="currency"
                :wallet="wallet"
                :withdraw="withdraw"
                :confirm="confirm"
                @wallet="onWallet"
                @refresh="refresh"
                @close="onComponent"
                @component="onComponent"
                @confirm="onConfirm"
            />
        </transition>
    </div>
</template>

<script>
import UIPageHeader from "@/components/ui/page-header/page-header.vue";
import WalletHeader from "@/components/shared/wallet/header/header.vue";
import WalletTable from "@/components/shared/wallet/table/table.vue";
import WalletSwap from "@/components/shared/wallet/swap/swap.vue";
import WalletDeposit from "@/components/shared/wallet/deposit/deposit.vue";
import WalletWithdraw from "@/components/shared/wallet/withdraw/withdraw.vue";
import WalletWithdrawRubBank from "@/components/shared/wallet/withdraw/rub-bank.vue";
import WalletConfirm from "@/components/shared/wallet/confirm/confirm.vue";
import WalletImexFrame from "@/components/shared/wallet/withdraw/imex-frame.vue";

import Api from "@/api/api.js";

const api = new Api();

export const COMPONENTS = {
    swap: "swap",
    deposit: "deposit",
    withdraw: "withdraw",
    withdrawRub: "withdrawRub",
    confirm: "confirm",
    imexFrame: "imexFrame",
    default: "",
};

export default {
    name: "WalletContent",
    components: {
        UIPageHeader,
        WalletHeader,
        WalletTable,
        WalletSwap,
        WalletDeposit,
        WalletWithdraw,
        WalletWithdrawRubBank,
        WalletConfirm,
        WalletImexFrame,
    },
    props: {
        wallet: {
            type: Object,
            required: true,
        },
        transactions: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        loadingNewPage: {
            type: Boolean,
            default: false,
        },
        show: {
            type: Boolean,
            default: false,
        },
        detailed: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            component: COMPONENTS.default,
            currency: null,
            withdraw: null,
            confirm: null,
            pendingImex: [],
            poolLiquidity: null,
        };
    },
    mounted() {
        this.loadPendingImex();
        this.loadPoolLiquidity();
    },
    computed: {
        getComponent() {
            switch (this.component) {
                case COMPONENTS.swap:
                    return WalletSwap;
                case COMPONENTS.deposit:
                    return WalletDeposit;
                case COMPONENTS.withdraw:
                    return WalletWithdraw;
                case COMPONENTS.withdrawRub:
                    return WalletWithdrawRubBank;
                case COMPONENTS.confirm:
                    return WalletConfirm;
                case COMPONENTS.imexFrame:
                    return WalletImexFrame;
                default:
                    return COMPONENTS.default;
            }
        },
    },
    methods: {
        onDeposit(deposit) {
            this.currency = deposit;

            this.onComponent(COMPONENTS.deposit);
        },

        onWithdraw(withdraw) {
            this.currency = withdraw;

            this.onComponent(COMPONENTS.withdraw);
        },

        onWithdrawRub() {
            this.onComponent(COMPONENTS.withdrawRub);
        },

        onComponent(component) {
            this.component = component;
        },

        onWallet(wallet) {
            this.$emit("wallet", wallet);
        },

        refresh() {
            this.$emit("refresh");
        },

        onConfirm(confirm) {
            this.confirm = confirm;
        },

        onChangePage() {
            this.$emit("page");
        },

        onChangeDetailed() {
            this.$emit("detailed");
        },

        async loadPendingImex() {
            try {
                const list = await api.pendingImexWithdrawals();
                this.pendingImex = list || [];
            } catch (_) {
                // silently ignore — feature is optional
            }
        },

        async loadPoolLiquidity() {
            try {
                const amount = await api.imexPoolLiquidity();
                this.poolLiquidity = amount !== undefined && amount !== null ? amount : null;
            } catch (_) {
                // silently ignore — informational only, not shown for non-IMEX users
            }
        },

        onResumeImex(withdrawal) {
            this.confirm = {id: withdrawal.id, imexFrameUrl: withdrawal.imexFrameUrl};
            this.onComponent(COMPONENTS.imexFrame);
        }
    },
};
</script>

<style lang="scss">
.wallet-content {
    display: flex;
    flex-direction: column;
    grid-row-gap: 24px;

    .wallet-content__imex-banner {
        background: #fff8e1;
        border: 1px solid #ffc107;
        border-radius: 12px;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
        font-size: 13px;
    }

    .wallet-content__pool-liquidity {
        color: $txt-light;
        font-size: 12px;
        padding: 0 4px;
    }

    .wallet-content__imex-resume {
        @include button-reset;
        font-size: 13px;
        color: $main;
        border: 1px solid $main;
        border-radius: 8px;
        padding: 5px 12px;
        cursor: pointer;

        &:hover {
            background: $main;
            color: $white;
        }
    }

    .wallet-content__body {
        background: #ffffff;
        border-radius: 16px;
        padding: 20px 20px 28px 20px;
        display: flex;
        flex-direction: column;
        grid-row-gap: 20px;
    }

    .wallet-content__modal {
        @include modal;
    }
}

@media screen and (max-width: 768px) {
    .wallet-content {
        grid-row-gap: 28px;

        .wallet-content__body {
            padding: 0 0 28px 0;
            grid-row-gap: 24px;
        }
    }
}
</style>
